import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Space, Table, message, Select } from "antd";
import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Toasts from "../../../core/common/toast";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import {
  addLanguageTranslation,
  bulkUpdateTranslations,
  getLanguageGroups,
  getLanguageTranslations,
  getLanguageVersions,
  publishTranslation
} from "../../../core/data/redux/languageSlice";
import { all_routes } from "../../router/all_routes";
import TransJsonModal from "./TransJsonModal";
import { useDebounce } from "../../../core/hooks/useDebounce";


const LanguagePhrasePage = () => {
  const [data, setData] = useState<any[]>([]);
  const [translationsData, setTranslationsData] = useState<any[]>([]); // Track API data for updates
  const [searchText, setSearchText] = useState<string>(""); // Search input state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const routes = all_routes;
  const [isUpdating, setIsUpdating] = useState(false);
  const location = useLocation();
  const editingLanguage = location.state?.editingLanguage;
  const languagesState = useSelector((state: any) => state.languageState);
  const languageTranslationsData = languagesState?.languageTranslationsData;
  const languageGroupsData = languagesState?.groupsData;
  const addLanguageTranslationData = languagesState?.addLanguageTranslationData || null;
  const updateLanguageTranslationData = languagesState?.updateLanguageTranslationData || null;
  const deleteLanguageTranslationData = languagesState?.deleteLanguageTranslationData || null;
  const bulkUpdateTranslationsData = languagesState?.bulkUpdateTranslationsData || null;
  const dispatch = useDispatch();
  const [isJsonModalVisible, setIsJsonModalVisible] = useState(false);
  const navigation = useNavigate();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  useEffect(() => {
    if (!editingLanguage) {
      navigation(routes.language.list);
    }
  }, []);

  // Consolidated data setting logic into the effect at line 122


  // Function to fetch translations with query
  const fetchTranslations = useCallback((page: number = 1, query: string = "", groupId: string | null = null, versionId: string | null = null) => {
    const payload: any = {
      page: page,
      languageId: editingLanguage?.id || "",
    };

    // Add filters to payload
    if (query.trim()) payload.keyword = query.trim();

    // Use passed values or current state
    const gId = groupId !== undefined ? groupId : selectedGroup;
    const vId = versionId !== undefined ? versionId : selectedVersion;

    if (gId) payload.group_id = gId;
    if (vId) payload.version_id = vId;

    dispatch(getLanguageTranslations(payload));
  }, [editingLanguage?.id, dispatch, selectedGroup, selectedVersion]);

  // 🔹 Handle page or pageSize change
  const handleTableChange = (newPagination: any) => {
    setPagination(newPagination);
    fetchTranslations(newPagination.current, searchText, selectedGroup, selectedVersion);
  };

  const handlePublish = () => {
    dispatch(publishTranslation({ language_id: editingLanguage.id }))
  }

  useEffect(() => {
    if (languagesState.loading === 'fulfilled' && languagesState.action === 'translation_publish') {
      toast.success('Translation published successfully')
    }
  }, [languagesState.loading]);



  useEffect(() => {
    dispatch(getLanguageGroups({}));
    dispatch(getLanguageVersions({ language: editingLanguage?.id || editingLanguage?.name }));
    
    // Initial fetch only if editingLanguage exists
    if (editingLanguage) {
      // Use the functional version to avoid resetting state
      const payload: any = {
        page: 1,
        languageId: editingLanguage.id,
      };
      dispatch(getLanguageTranslations(payload));
    }
  }, [editingLanguage?.id, dispatch]); // Only run when language ID changes

  // Update translationsData and pagination when API data is received
  useEffect(() => {
    if (languageTranslationsData?.data) {
      // Create deep copy to avoid read-only issues
      const deepCopy = languageTranslationsData.data.map((item: any) => ({
        ...item,
      }));
      setTranslationsData(deepCopy);
      
      // Update pagination
      setPagination((prev) => ({
        ...prev,
        total: languageTranslationsData.total || 0,
        current: languageTranslationsData.page || 1,
        pageSize: languageTranslationsData.limit || 10,
      }));
    } else if (languageTranslationsData?.result) {
      // Create deep copy to avoid read-only issues
      const deepCopy = languageTranslationsData.result.map((item: any) => ({
        ...item,
      }));
      setTranslationsData(deepCopy);
    }
  }, [languageTranslationsData]);

  // Debounced search function to call API with keyword
  const debouncedSearch = useDebounce((query: string) => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchTranslations(1, query, selectedGroup, selectedVersion);
  }, 700);

  // Handle specific response data - following the main language page pattern
  useEffect(() => {
    if (addLanguageTranslationData?.status === 201 || addLanguageTranslationData?.status === 200) {
      setIsModalVisible(false);
      form.resetFields();
      fetchTranslations(pagination.current, searchText);
    }
  }, [addLanguageTranslationData, dispatch, form]);

  useEffect(() => {
    if (updateLanguageTranslationData?.status === 200) {
      fetchTranslations(pagination.current, searchText);
    }
  }, [updateLanguageTranslationData, dispatch]);

  useEffect(() => {
    if (deleteLanguageTranslationData?.status === 200) {
      fetchTranslations(pagination.current, searchText);
    }
  }, [deleteLanguageTranslationData, dispatch]);

  useEffect(() => {
    if (bulkUpdateTranslationsData?.status === 200) {
      setIsUpdating(false);
      fetchTranslations(pagination.current, searchText);
    }
  }, [bulkUpdateTranslationsData, dispatch]);

  // Handle successful operations - following existing pattern
  useEffect(() => {
    console.log("🔍 Full Redux State Debug:", languagesState);
    console.log("🔍 Redux State Debug:", {
      loading: languagesState.loading,
      action: languagesState.action,
      error: languagesState.error,
      hasTranslationsData: !!languageTranslationsData,
      addLanguageTranslationData: languagesState?.addLanguageTranslationData,
      updateLanguageTranslationData: languagesState?.updateLanguageTranslationData,
      deleteLanguageTranslationData: languagesState?.deleteLanguageTranslationData,
      bulkUpdateTranslationsData: languagesState?.bulkUpdateTranslationsData
    });

    if (languagesState.loading === "fulfilled") {
      if (languagesState.action === "add-language-translation") {
        setIsModalVisible(false);
        form.resetFields();
        // Refresh data with current search keyword
        fetchTranslations(pagination.current, searchText);
      } else if (languagesState.action === "update-language-translation") {
        // Refresh data with current search keyword
        fetchTranslations(pagination.current, searchText);
      } else if (languagesState.action === "delete-language-translation") {
        // Refresh data with current search keyword
        fetchTranslations(pagination.current, searchText);
      } else if (languagesState.action === "bulk-update-translations") {
        setIsUpdating(false);
        // Refresh data with current search keyword
        fetchTranslations(pagination.current, searchText);
      }
    } else if (languagesState.loading === "rejected") {
      console.log("❌ Error occurred:", languagesState.error);
      if (languagesState.action === "bulk-update-translations") {
        setIsUpdating(false);
      }
    }
  }, [
    languagesState.loading,
    languagesState.action,
    languagesState.error,
    dispatch,
    form,
  ]);

  const handleAddNew = () => {
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const newEntries = values.strings.map((item: any, index: any) => ({
        key: data.length + index + 1,
        langString: item.key,
        value: item.value,
      }));

      // Add via API using the correct specification
      for (const entry of newEntries) {
        // Try to get group_id from various possible sources
        let groupId =
          editingLanguage?.group_id ||
          editingLanguage?.language_group_id ||
          editingLanguage?.groupId;

        // If no group_id in language data, try to find it from groups data
        if (!groupId && languageGroupsData?.data) {
          const groups = languageGroupsData.data;
          // Find group by language group name or default to first group
          const matchingGroup = groups.find(
            (group: any) => group.group_name === editingLanguage?.group
          );
          groupId = matchingGroup?.id || groups[0]?.id || "1";
        }

        // Fallback to "1" if still no group_id
        if (!groupId) {
          groupId = "1";
        }

        const payload = {
          language_id: editingLanguage?.id || "",
          group_id: groupId,
          translation_key: entry.langString,
          translation_value: entry.value,
          status: true, // Boolean value - active status
        };
        dispatch(addLanguageTranslation(payload));
      }

      setData((prev) => [...prev, ...newEntries]);
      message.success("New language strings added successfully");
      setIsModalVisible(false);
    } catch (error) {
      console.error("Validation Failed:", error);
    }
  };

  const handleUpdate = () => {
    // Use translationsData (original data) for updates, not filtered data
    if (!translationsData.length) {
      message.warning("No translations to update");
      return;
    }

    setIsUpdating(true);

    // Prepare translations for bulk update API - only send required fields
    const translations = translationsData.map((item) => ({
      translation_key: item.translation_key,
      translation_value: item.translation_value,
      // Don't include id or status as they cause 400 errors
    }));

    const payload = {
      language_id: editingLanguage?.id || "",
      translations: translations,
    };

    dispatch(bulkUpdateTranslations(payload));
  };

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 100,
      render: (text: string, record: any, index: number) => (
        <>
          <div>
            {pagination.current
              ? index +
              pagination.current * pagination.pageSize -
              pagination.pageSize +
              1
              : index + 1}
          </div>
        </>
      ),
      //sorter: (a: any, b: any) => a.id.length - b.id.length,
    },
    {
      title: "Lang string",
      dataIndex: "translation_key",
      render: (text: any) => (
        <div
          style={{
            //   backgroundColor: '#f5f5f5',
            padding: "6px 12px",
            //   borderRadius: '4px',
            color: "#999",
            cursor: "not-allowed",
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: "Value",
      dataIndex: "translation_value",
      render: (text: any, record: any, index: any) => {
        // Find the actual index in translationsData by matching the record
        const actualIndex = translationsData.findIndex(
          (item) => item.translation_key === record.translation_key
        );

        return (
          <Input
            value={text}
            onChange={(e) => {
              // Update the translationsData for API calls - create deep copy to avoid read-only issues
              const newTranslationsData = translationsData.map((item, idx) =>
                idx === actualIndex
                  ? { ...item, translation_value: e.target.value }
                  : item
              );
              setTranslationsData(newTranslationsData);

              // Also update the original data state for backward compatibility
              const newData = [...data];
              if (newData[actualIndex]) {
                newData[actualIndex] = { ...newData[actualIndex], value: e.target.value };
                setData(newData);
              }
            }}
          />
        );
      },
    },
  ];


  const handleIsJsonModalVisible = (value: boolean) => {
    setIsJsonModalVisible(value);
  }

  const handleSearch = (value: string) => {
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleGroupChange = (value: string | null) => {
    setSelectedGroup(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchTranslations(1, searchText, value, selectedVersion);
  };

  const handleVersionChange = (value: string | null) => {
    setSelectedVersion(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchTranslations(1, searchText, selectedGroup, value);
  };
  return (
    <div className="page-wrapper">
      {/* Error Toast */}
      <Toasts
        show={languagesState.loading === "rejected"}
        message={languagesState.error || "Operation failed"}
        title={"Failed"}
        type={"danger"}
      />

      {/* Success Toasts using specific data responses */}
      <Toasts
        show={addLanguageTranslationData?.status === 201 || addLanguageTranslationData?.status === 200}
        message={addLanguageTranslationData?.message || "Translation added successfully!"}
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={updateLanguageTranslationData?.status === 200}
        message={updateLanguageTranslationData?.message || "Translation updated successfully!"}
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={deleteLanguageTranslationData?.status === 200}
        message={deleteLanguageTranslationData?.message || "Translation deleted successfully!"}
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={bulkUpdateTranslationsData?.status === 200}
        message={bulkUpdateTranslationsData?.message || "All translations updated successfully!"}
        title={"Success"}
        type={"success"}
      />

      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {`Language phrase update (${editingLanguage?.name || "Unknown Language"
                })`}
            </h3>

            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.language.list}>Languages</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {`Language phrase update (${editingLanguage?.name || "Unknown Language"
                    })`}

                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">

            <Button
              type="primary"
              onClick={handlePublish}
              icon={<i className="ti ti-square-rounded-plus me-2" />}
              style={{
                backgroundColor: "#c0282c",
                border: "1px solid #c0282c",
                fontWeight: "500",
              }}
            >
              Publish Translation
            </Button>

            <Button
              type="primary"
              onClick={handleAddNew}
              icon={<i className="ti ti-square-rounded-plus me-2" />}
              style={{
                backgroundColor: "#c0282c",
                border: "1px solid #c0282c",
                fontWeight: "500",
              }}
            >
              Add String
            </Button>

            <Button
              type="primary"
              onClick={() => setIsJsonModalVisible(true)}
              icon={<i className="ti ti-square-rounded-plus me-2" />}
              style={{
                backgroundColor: "#c0282c",
                border: "1px solid #c0282c",
                fontWeight: "500",
              }}
            >
              Upload Json
            </Button>

          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-4 mt-3">String List</h4>
          </div>
          <div className="card-body p-0 py-3">
            {/* Search Input */}
            <div className="px-3 mb-3 d-flex gap-3 flex-wrap align-items-center">
              <Input
                placeholder="Search by key or value..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                allowClear
                style={{ maxWidth: "300px" }}
              />
              <Select
                placeholder="Select Group"
                style={{ width: 200 }}
                allowClear
                onChange={handleGroupChange}
                options={languageGroupsData?.data?.map((g: any) => ({
                  label: g.group_name,
                  value: g.id,
                }))}
              />
              <Select
                placeholder="Select Version"
                style={{ width: 200 }}
                allowClear
                onChange={handleVersionChange}
                options={languagesState?.versionsData?.data?.map((v: any) => ({
                  label: v.version,
                  value: v.id,
                }))}
              />
            </div>
            <Table
              columns={columns}
              dataSource={
                translationsData.length > 0
                  ? translationsData
                  : languageTranslationsData?.data || []
              }
              loading={languagesState.loading === "pending"}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: false,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              onChange={handleTableChange}

              bordered
              style={{ marginTop: "20px" }}
            />

          </div>
          <div className="p-0 py-2 px-5 mb-2" style={{ textAlign: "right" }}>
            <Button
              type="primary"
              style={{
                backgroundColor: "#c0282c",
                border: "1px solid #c0282c",
                fontWeight: "500",
              }}
              onClick={handleUpdate}
              loading={isUpdating}
            >
              {isUpdating ? "Please wait..." : "Update"}
            </Button>
          </div>
        </div>

        {/* Modal */}
        <Modal
          title="Manage Strings"
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={() => setIsModalVisible(false)}
          width={600}
        >
          <Form form={form} layout="vertical" name="dynamic_form_nest_item">
            <Form.List name="strings" initialValue={[{ key: "", value: "" }]}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space
                      key={key}
                      style={{ display: "flex", marginBottom: 8 }}
                      align="baseline"
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "key"]}
                        rules={[{ required: true, message: "Missing key" }]}
                      >
                        <Input placeholder="Key" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "value"]}
                        rules={[{ required: true, message: "Missing value" }]}
                      >
                        <Input placeholder="Value" />
                      </Form.Item>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    </Space>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block>
                      Add String
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form>
        </Modal>
        <TransJsonModal
          isJsonModalVisible={isJsonModalVisible}
          handleIsJsonModalVisible={(val: boolean) => handleIsJsonModalVisible(val)}
          languageId={editingLanguage?.id}
        />
      </div>
    </div>
  );
};

export default LanguagePhrasePage;
